import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';

const FeedbackManagement = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    minRating: 0,
    status: 'all',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

  // Fetch feedback data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all feedback regardless of status
        const feedbackRes = await fetch(`${BQ_BASE_URL}/bq/api/feedback/`);

        if (!feedbackRes.ok) {
          throw new Error(`Feedback API error: ${feedbackRes.status}`);
        }

        const feedbackData = await feedbackRes.json();

        // Initialize status if missing (default to "new")
        const feedbackWithStatus = feedbackData.map((item) => ({
          ...item,
          status: item.status || 'new', // Ensure every item has a status
        }));

        setFeedback(feedbackWithStatus);

        // Fetch stats
        const statsRes = await fetch(`${BQ_BASE_URL}/bq/api/feedback/stats/ratings`);

        if (!statsRes.ok) {
          throw new Error(`Stats API error: ${statsRes.status}`);
        }

        const statsData = await statsRes.json();

        // Validate stats data structure
        if (statsData && statsData.average_ratings) {
          setStats(statsData);
        } else {
          console.warn('Stats API returned unexpected structure:', statsData);
          setStats(null);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setStats(null); // Ensure stats is null on error
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update your filter function to properly handle status filtering
  const filteredFeedback = feedback.filter((item) => {
    // First filter by minimum rating
    const meetsRating = item.overall_rating >= filters.minRating;

    // Then filter by status if not "all"
    const meetsStatus = filters.status === 'all' || item.status === filters.status;

    return meetsRating && meetsStatus;
  });

  // Modify your status update function to ensure it updates the state correctly
  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/feedback/${feedbackId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        setFeedback((prev) =>
          prev.map((item) =>
            item.feedback_id === feedbackId
              ? { ...item, status } // Directly use the new status
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  // Prepare chart data with safe access
  const ratingChartData = stats?.average_ratings
    ? [
        { name: 'Overall', value: stats.average_ratings.overall || 0 },
        { name: 'Cleanliness', value: stats.average_ratings.cleanliness || 0 },
        { name: 'Value', value: stats.average_ratings.value_for_money || 0 },
        { name: 'Service', value: stats.average_ratings.service || 0 },
        { name: 'Location', value: stats.average_ratings.location || 0 },
      ]
    : [];

  // Get current feedback for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredFeedback.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFeedback.length / itemsPerPage);

  // Rating cards data with safe access
  const ratingCards = stats?.average_ratings
    ? [
        {
          title: 'Overall Rating',
          value: stats.average_ratings.overall || 0,
          color: 'bg-white',
          textColor: 'text-gray-950',
        },
        {
          title: 'Cleanliness',
          value: stats.average_ratings.cleanliness || 0,
          color: 'bg-white',
          textColor: 'text-gray-950',
        },
        {
          title: 'Value for Money',
          value: stats.average_ratings.value_for_money || 0,
          color: 'bg-white',
          textColor: 'text-gray-950',
        },
        {
          title: 'Service',
          value: stats.average_ratings.service || 0,
          color: 'bg-white',
          textColor: 'text-gray-950',
        },
        {
          title: 'Location',
          value: stats.average_ratings.location || 0,
          color: 'bg-white',
          textColor: 'text-gray-950',
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header - kept exactly as is */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Feedback Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/feedback&queries/feedback-management')}
            className="btn text-black font-bold"
          >
            Feedback management
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/feedback&queries/queries-management')}
            className="btn"
          >
            Queries management
          </button>
        </div>
      </header>

      <main className=" px-4 py-6 sm:px-6 lg:px-2">
        {/* Rating Cards Section */}
        {stats?.average_ratings && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {ratingCards.map((card, index) => (
              <div key={index} className={`${card.color} p-4 rounded-lg shadow-lg`}>
                <h3 className="text-sm font-medium text-gray-600">{card.title}</h3>
                <div className="flex items-center mt-2">
                  <span className={`text-2xl font-bold ${card.textColor}`}>
                    {card.value.toFixed(1)}
                  </span>
                  <div className="ml-2 flex">
                    {[...Array(5)].map((_, i) => {
                      if (card.value >= i + 1) {
                        return <FaStar key={i} className="text-yellow-400 w-4 h-4" />;
                      } else if (card.value > i) {
                        return <FaStarHalfAlt key={i} className="text-yellow-400 w-4 h-4" />;
                      } else {
                        return <FaRegStar key={i} className="text-gray-300 w-4 h-4" />;
                      }
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters and Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Filters */}
          <div className="bg-white shadow rounded-lg p-4 col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Rating
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={filters.minRating}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      minRating: Number(e.target.value),
                    });
                    setCurrentPage(1); // Reset to first page when filters change
                  }}
                >
                  <option value={0}>All Ratings</option>
                  <option value={1}>1 Stars</option>
                  <option value={2}>2 Stars</option>
                  <option value={3}>3 Stars</option>
                  <option value={4}>4 Stars</option>
                  <option value={5}>5 Stars</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm "
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                    setCurrentPage(1); // Reset to first page when filters change
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Feedback Summary</h3>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-100 p-3 rounded-lg">
                  <p className="text-xs text-gray-950 font-medium">Total Feedback</p>
                  <p className="text-xl font-bold text-gray-900">{stats.total_feedbacks}</p>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <p className="text-xs text-gray-950 font-medium">Recommendation Rate</p>
                  <p className="text-xl font-bold text-gray-900">{stats.recommendation_rate}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No statistics available</p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feedback List */}
          <div className="bg-white shadow rounded-lg overflow-hidden lg:col-span-2">
            <div className="px-2 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-2 text-gray-900">Guest Feedback</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : currentItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No feedback matching your filters
                </div>
              ) : (
                currentItems.map((item) => (
                  <div
                    key={item.feedback_id}
                    className="p-4 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <div className="flex  justify-between items-start">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-900 capitalize">{item.name}</h4>
                          <span className="text-xs text-gray-500">
                            {new Date(item.submission_date).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{item.email}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Reservation ID: {item.order_id}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            item.overall_rating >= 4
                              ? 'bg-green-100 text-green-800'
                              : item.overall_rating >= 2
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {item.overall_rating} ★
                        </span>
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-xs "
                          value={item.status || 'new'}
                          onChange={(e) => updateFeedbackStatus(item.feedback_id, e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-3">
                      <p className="text-sm text-gray-700">
                        {item.message || 'No message provided'}
                      </p>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          item.would_recommend
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.would_recommend ? 'Would recommend' : 'Would not recommend'}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          item.would_return
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.would_return ? 'Would return' : 'Would not return'}
                      </span>
                      {item.is_contact_allowed && (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          Contact allowed
                        </span>
                      )}
                    </div>

                    {/* Detailed Ratings */}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="text-xs">
                        <span className="font-medium">Cleanliness: </span>
                        <span
                          className={`${
                            item.cleanliness_rating >= 4
                              ? 'text-green-600'
                              : item.cleanliness_rating >= 2
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {item.cleanliness_rating} ★
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="font-medium">Value: </span>
                        <span
                          className={`${
                            item.value_for_money_rating >= 4
                              ? 'text-green-600'
                              : item.value_for_money_rating >= 2
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {item.value_for_money_rating} ★
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="font-medium">Service: </span>
                        <span
                          className={`${
                            item.service_rating >= 4
                              ? 'text-green-600'
                              : item.service_rating >= 2
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {item.service_rating} ★
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="font-medium">Location: </span>
                        <span
                          className={`${
                            item.location_rating >= 4
                              ? 'text-green-600'
                              : item.location_rating >= 2
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {item.location_rating} ★
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {filteredFeedback.length > itemsPerPage && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredFeedback.length)}
                      </span>{' '}
                      of <span className="font-medium">{filteredFeedback.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                      >
                        <span className="sr-only">First</span>
                        &laquo;
                      </button>
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                      >
                        <span className="sr-only">Previous</span>
                        &lsaquo;
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                      >
                        <span className="sr-only">Next</span>
                        &rsaquo;
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                      >
                        <span className="sr-only">Last</span>
                        &raquo;
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Ratings Chart */}
          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Ratings Analysis</h3>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : stats?.average_ratings ? (
              <div className="flex flex-col gap-6">
                {/* Bar Chart with monochrome styling */}
                <div className="w-full">
                  <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
                    Average Ratings Breakdown
                  </h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ratingChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          domain={[0, 5]}
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            fontSize: '12px',
                          }}
                        />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Rating">
                          {ratingChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                entry.value < 3
                                  ? '#ef4444'
                                  : entry.value < 4
                                    ? '#f59e0b'
                                    : '#10b981'
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center mt-3 gap-1 flex-col">
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 rounded-full bg-green-500 mr-1"></div>
                      <span>Good (4+ stars)</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 rounded-full bg-yellow-500 mr-1"></div>
                      <span>Needs work (3-4 stars)</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 rounded-full bg-red-500 mr-1"></div>
                      <span>Critical (-3 stars)</span>
                    </div>
                  </div>
                </div>
                {/* Pie Chart for Improvement Areas */}
                <div className="w-full">
                  <h4 className="text-md font-medium text-gray-700 mb-2 text-center">
                    Areas Needing Improvement
                  </h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={ratingChartData.filter((item) => item.value < 4)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          dataKey="value"
                          nameKey="name"

                          label={({ name, percent, x, y }) => (
                            <text
                              x={x}
                              y={y}
                              fill="#333"
                              fontSize="12" // 👈 smaller text size
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {`${name}`}
                            </text>
                          )}
                          labelPosition="outside"
                        >
                          {ratingChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                entry.value < 3
                                  ? '#ef4444'
                                  : entry.value < 4
                                    ? '#f59e0b'
                                    : '#10b981'
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            fontSize: '12px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    {ratingChartData.filter((item) => item.value < 4).length > 0 ? (
                      <ul className="list-disc pl-5 space-y-1">
                        {ratingChartData
                          .filter((item) => item.value < 4)
                          .sort((a, b) => a.value - b.value)
                          .map((item, index) => (
                            <li key={index} className="flex items-start">
                              <span className="inline-flex items-center mr-2">
                                <span
                                  className="w-3 h-3 rounded-full mr-1 inline-block"
                                  style={{
                                    backgroundColor: item.value < 3 ? '#ef4444' : '#f59e0b',
                                  }}
                                ></span>
                                <span className="text-sm">{item.name}</span>
                              </span>
                              <span>
                                ({item.value.toFixed(1)} stars) - Needs{' '}
                                {item.value < 3 ? 'urgent' : ''} improvement
                              </span>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="text-center text-green-600">
                        All areas meet satisfactory ratings (4+ stars)!
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No ratings data available</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FeedbackManagement;
